// Relógio de rolar (pedido 2026-10-21: "escolher o horário rolando, igual à
// agenda do Google"): duas colunas (hora 00–23, minuto de 5 em 5) com
// scroll-snap; o item que para no meio é o escolhido.
import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

const ITEM = 44;
const VISIBLE = 5;
const pad = (n: number) => String(n).padStart(2, "0");
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

function Column({ values, value, onChange, label }: { values: number[]; value: number; onChange: (v: number) => void; label: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const settle = useRef<ReturnType<typeof setTimeout> | null>(null);
  const index = Math.max(0, values.indexOf(value));

  // Posição inicial / quando o valor muda por fora.
  useEffect(() => {
    const el = ref.current;
    if (el && Math.round(el.scrollTop / ITEM) !== index) el.scrollTo({ top: index * ITEM });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  return (
    <div className="relative flex-1">
      <div
        ref={ref}
        role="listbox"
        aria-label={label}
        onScroll={(e) => {
          const el = e.currentTarget;
          if (settle.current) clearTimeout(settle.current);
          settle.current = setTimeout(() => {
            const i = Math.min(values.length - 1, Math.max(0, Math.round(el.scrollTop / ITEM)));
            if (values[i] !== value) onChange(values[i]!);
          }, 90);
        }}
        className="h-full snap-y snap-mandatory overflow-y-auto overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ height: ITEM * VISIBLE, paddingTop: ITEM * 2, paddingBottom: ITEM * 2 }}
      >
        {values.map((v) => (
          <button
            key={v}
            type="button"
            role="option"
            aria-selected={v === value}
            onClick={() => {
              onChange(v);
              ref.current?.scrollTo({ top: values.indexOf(v) * ITEM, behavior: "smooth" });
            }}
            className={cn(
              "flex w-full snap-center items-center justify-center tabular-nums transition-all",
              v === value ? "text-[28px] font-extrabold text-slate-900 dark:text-white" : "text-xl font-semibold text-slate-400",
            )}
            style={{ height: ITEM }}
          >
            {pad(v)}
          </button>
        ))}
      </div>
    </div>
  );
}

export function TimeWheel({ value, onChange }: { value: string; onChange: (hhmm: string) => void }) {
  const [h, m] = value.split(":").map(Number) as [number, number];
  // Minuto fora do passo de 5 (ex.: 14:07 vindo da IA) cai no mais próximo.
  const m5 = Math.min(55, Math.round(m / 5) * 5);
  return (
    <div className="relative flex items-center gap-2 rounded-3xl bg-slate-50 px-4 dark:bg-slate-800/60" style={{ height: ITEM * VISIBLE }}>
      {/* Faixa do item escolhido */}
      <div className="pointer-events-none absolute inset-x-3 top-1/2 -translate-y-1/2 rounded-2xl ai-soft ring-1 ai-border" style={{ height: ITEM }} aria-hidden />
      {/* Esmaece em cima e embaixo */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-16 rounded-t-3xl bg-gradient-to-b from-slate-50 to-transparent dark:from-slate-800" aria-hidden />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-16 rounded-b-3xl bg-gradient-to-t from-slate-50 to-transparent dark:from-slate-800" aria-hidden />
      <Column label="Hora" values={HOURS} value={h} onChange={(v) => onChange(`${pad(v)}:${pad(m5)}`)} />
      <span className="relative z-0 text-3xl font-extrabold ai-text">:</span>
      <Column label="Minuto" values={MINUTES} value={m5} onChange={(v) => onChange(`${pad(h)}:${pad(v)}`)} />
    </div>
  );
}
